/**
 * Your data, yours: a copy of everything, and the way to delete all of it.
 *
 * Both live on the dashboard rather than behind a settings page, because a
 * promise kept somewhere hard to find is a promise most people never test.
 * The deletion asks for the phrase typed out: a button alone is not consent
 * to erase a year of records, and there is no undo.
 */

import { useState } from "react";
import { api, apiFile, saveBlob, ApiError } from "./auth";
import { TextField } from "./fields";

const PHRASE = "delete my account";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

export function AccountSection({ onDeleted }: { onDeleted: () => Promise<void> }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exported, setExported] = useState(false);

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const exportData = () => {
    setExporting(true);
    setExportError(null);
    setExported(false);
    apiFile("/api/account/export")
      .then(({ blob, filename }) => { saveBlob(blob, filename); setExported(true); })
      .catch((err) => setExportError(describe(err)))
      .finally(() => setExporting(false));
  };

  const ready = confirm.trim().toLowerCase() === PHRASE;

  const deleteAccount = () => {
    if (!ready || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    api<{ deleted: boolean }>("/api/account/delete", { method: "POST", body: JSON.stringify({ confirm }) })
      .then(() => onDeleted())
      .catch((err) => { setDeleteError(describe(err)); setDeleting(false); });
  };

  return (
    <section>
      <h2>Your data</h2>
      <p className="muted small">
        Everything JobCork holds about you — resume text, answers, preferences, every application and its
        record — as one file you keep. Or delete the account and all of it at once.
      </p>
      <div className="row">
        <button className="btn ghost" onClick={exportData} disabled={exporting}>
          {exporting ? "Preparing your file…" : "Download a copy"}
        </button>
        {!open && (
          <button className="btn ghost" onClick={() => setOpen(true)}>Delete my account</button>
        )}
      </div>
      {exported && <p className="muted small">Saved. It is a JSON file; every table is in it, even the empty ones.</p>}
      {exportError && <p className="err">{exportError}</p>}

      {open && (
        <div className="notice">
          <p>
            This removes your resume files, your answers, your preferences, your matches, your applications and
            the record of what was sent for each — everything — right away. There is no undo. Download a copy
            first if you want one.
          </p>
          <TextField
            label={`Type "${PHRASE}" to confirm`}
            value={confirm}
            onChange={setConfirm}
            placeholder={PHRASE}
          />
          <div className="row">
            <button className="btn" onClick={deleteAccount} disabled={!ready || deleting}>
              {deleting ? "Deleting…" : "Delete everything"}
            </button>
            <button className="btn ghost" onClick={() => { setOpen(false); setConfirm(""); }} disabled={deleting}>
              Keep my account
            </button>
          </div>
          {deleteError && <p className="err">{deleteError}</p>}
        </div>
      )}
    </section>
  );
}
